import { ReactElement, ReactNode } from 'react';

import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

/**
 * Marks content only admins can see. Use it through GratiSection's `adminOnly` prop: the tag in the
 * header, or the strip at the top of the open section when it carries an action (e.g. Publish).
 */
export interface GratiAdminOnlyProps {
  /** Only when the content can become visible to everyone, e.g. "Draft" until it's published.
   *  Content that is always admin-only has no status. */
  status?: string;
  /** What makes it visible, e.g. a Publish button. Shown at the end of the strip. */
  action?: ReactNode;
}

const eyeIcon = <VisibilityOffIcon sx={{ fontSize: '1rem' }} />;

/** The strip at the top of an open section: "👁 Draft · admins only [Publish]". Tinted as a
 *  warning while there's a status (something is waiting to be made visible), neutral otherwise. */
export function GratiAdminOnlyStrip({ status, action }: GratiAdminOnlyProps): ReactElement {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        backgroundColor: (theme) => alpha(status ? theme.palette.warning.main : theme.palette.text.primary, status ? 0.12 : 0.06),
        borderRadius: 1,
        px: 1,
        py: 0.5,
        color: 'text.secondary',
      }}
    >
      {eyeIcon}
      <Typography variant="bodySmall" sx={{ flexGrow: 1 }}>
        {status ? `${status} · admins only` : 'Admins only'}
      </Typography>
      {action}
    </Stack>
  );
}

/** The header tag after the title: "· 👁 admins only", or "· 👁 Draft · admins only" with a status. */
export function GratiAdminOnlyTag({ status }: Pick<GratiAdminOnlyProps, 'status'>): ReactElement {
  return (
    <Stack
      direction="row"
      component="span"
      spacing={0.5}
      sx={{ alignItems: 'center', color: 'text.secondary', flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      <Typography variant="bodySmall" component="span">·</Typography>
      {eyeIcon}
      <Typography variant="bodySmall" component="span">{status ? `${status} · admins only` : 'admins only'}</Typography>
    </Stack>
  );
}
